import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { login, meKeys } from '../api/auth';
import { HatchlingPortrait } from '../companion/HatchlingPortrait';
import { ApiClientError } from '../api/client';
import { FormField } from './FormField';
import { deriveFormError } from './formErrors';
import { useOnline } from '../pwa/useOnline';
import { useMustRenameStore } from '../shell/mustRenameStore';

export function LoginScreen() {
  const nav = useNavigate();
  const location = useLocation();
  const state = location.state as { reason?: string; from?: string } | null;
  const sessionExpired = state?.reason === 'session-expired';
  // RequireHandleClaimed sends a signed-out visitor here with the route they wanted.
  const from = state?.from?.startsWith('/') ? state.from : '/habitat';
  const queryClient = useQueryClient();
  const isOnline = useOnline();
  const [fields, setFields] = useState({ email: '', password: '' });
  const m = useMutation({
    mutationFn: () => login(fields),
    // Decision #10 (task-15 brief): checked right here, before the shell ever renders — a
    // grandfathered account is routed straight to /claim-handle instead of its normal /habitat
    // destination. Sets the latch directly from THIS response (not waiting on AppShell's own /me
    // fetch) so there's no flash of the ordinary shell first.
    // Seeding `me` also clears the 401 RequireHandleClaimed cached on the way here — without it
    // the gate would read that stale error and bounce straight back to /login.
    onSuccess: (account) => {
      queryClient.setQueryData(meKeys.detail, account);
      useMustRenameStore.getState().setMustRename(account.mustRename);
      nav(account.mustRename ? '/claim-handle' : from, { replace: true });
    },
  });
  const apiErr = m.error instanceof ApiClientError ? m.error : undefined;
  const badCredentials = apiErr?.status === 401;
  // Bad-credentials already has its own dedicated (non-field) message below — don't
  // also run it through the generic fallback mapper.
  const { fieldErrors, formError } = badCredentials
    ? { fieldErrors: {}, formError: undefined }
    : deriveFormError(m.error);
  const set = (k: keyof typeof fields) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setFields(f => ({ ...f, [k]: e.target.value }));

  return (
    <main className="screen-form auth">
      <header className="auth-brand">
        <div className="auth-brand-pets" aria-hidden="true">
          <HatchlingPortrait code="vessin" />
          <HatchlingPortrait code="lumen" variant="joy" />
          <HatchlingPortrait code="kelpling" />
        </div>
        <h1>Project Pets</h1>
        <p>Raise, dress and adventure with your companion.</p>
      </header>
      {sessionExpired && <p className="form-note" role="status">You were signed out — log in again.</p>}
      <form onSubmit={e => { e.preventDefault(); m.mutate(); }}>
        <FormField label="Email" type="email" autoComplete="email" required
          value={fields.email} onChange={set('email')} disabled={m.isPending}
          error={fieldErrors.email} />
        <FormField label="Password" type="password" autoComplete="current-password" required
          value={fields.password} onChange={set('password')} disabled={m.isPending}
          error={fieldErrors.password} />
        {badCredentials && (
          <p className="field-error" role="alert">Email or password didn&rsquo;t match.</p>
        )}
        {formError && <p className="field-error" role="alert">{formError}</p>}
        <div className="actions">
          <button className="btn-primary" disabled={m.isPending || !isOnline}>
            {m.isPending ? 'Logging in…' : 'Log in'}
          </button>
          {!isOnline && <p className="form-note">You&rsquo;re offline — reconnect to log in.</p>}
          <Link to="/register" className="btn-quiet">New here? Create an account</Link>
        </div>
      </form>
    </main>
  );
}
